import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useAppContext } from "../context/AppContext";
import { CloseSharp as CloseIcon } from "@mui/icons-material";
import Toast from "./toast";
import { saveMedications } from "../helpers/UserChats";
import { MedicationIcon } from "./Icons";
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import styled from "@emotion/styled";
import { padding } from "@mui/system";

const AddMedicationsPopup = ({ open, handleClose, handleAddMedications, chatId }) => {
  const { isMobile } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [medications, setMedications] = useState([]);
  const [formValues, setFormValues] = useState({
    Medication: "",
    Dosage: "",
    Directions: "",
    Route: "",
    NeedHelp: true,
  });

  useEffect(() => {
    setMedications([]);
    setIsLoading(false);
    setFormValues({
      Medication: "",
      Dosage: "",
      Directions: "",
      Route: "",
      NeedHelp: true,
    });
  }, [open]);

  const handleSave = async () => {
    if (medications.length === 0) {
      Toast.error("Please add medication!");
      return;
    }

    // Logic to upload file
    try {
      setIsLoading(true);
      const payload = {
        chatId: chatId,
        medicationList: medications.map((med, index) => (
          {
            ...med,
            id: index + 1,
          }
      ))
      };

      const response = await saveMedications(payload);
      setIsLoading(false);
      if (response.statusCode === 200 && response.isSuccess === true) {
        Toast.success(response.message);
        setIsLoading(false);
        handleAddMedications(true);
      } else {
        Toast.error(response.message);
        setIsLoading(false);
      }
    } catch (error) {
      Toast.error(error.message);
      setIsLoading(false);
    }
  };

  const handleAddMedicine = () => {
    if (formValues.Medication.trim() === "") {
      Toast.error("Enter Medication");
      return;
    } else if (formValues.Dosage.trim() === "") {
      Toast.error("Enter Dosage");
      return;
    } else if (formValues.Directions.trim() === "") {
      Toast.error("Enter Directions");
      return;
    } else if (formValues.Route.trim() === "") {
      Toast.error("Enter Route");
      return;
    } else if (medications.length === 9) {
      Toast.error("Cannot enter more than nine medications!");
      return;
    }

    const _medications = medications;
    _medications.push({
      ...formValues,
      NeedHelp: formValues.NeedHelp === "Yes" ? true : false,
    });
    setMedications(_medications);
    setFormValues({
      Medication: "",
      Dosage: "",
      Directions: "",
      Route: "",
      NeedHelp: true,
    });
  };


  const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#65C466',
          opacity: 1,
          border: 0,
          ...theme.applyStyles('dark', {
            backgroundColor: '#2ECA45',
          }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[600],
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
        ...theme.applyStyles('dark', {
          opacity: 0.3,
        }),
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
      ...theme.applyStyles('dark', {
        backgroundColor: '#39393D',
      }),
    },
  }));

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "96%" : "60%",
    bgcolor: "#ffffff",
    // border: "2px solid #fff",
    borderRadius: "12px",
    maxHeight: "85vh", // set max height
    overflow: "hidden", // enable scrolling
    tableHead: {
      color: "#6B6B6B",
      fontSize: "14px",
      paddingY: "5px",
      paddingX: "10px",
      textTransform: "capitalize",
    },
    tableCell: {
      fontSize: "14px",
      paddingY: "5px",
      paddingX: "10px",
      textTransform: "capitalize",
    },
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" backgroundColor={"#FCFCFC"} padding={"10px 15px"} borderBottom="1px solid #D6D6D6">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{
              width: "40px",
              height: "40px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#7B7DE5",
              borderRadius: "10px",
            }}>
              <MedicationIcon />
            </Box>
            <div>
              <Typography fontSize={20} fontWeight={600}>
                Add Medication
              </Typography>

              <Typography variant="p" color="text.secondary">
                Add and review patient medications
              </Typography>
            </div>
          </Box>

          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{
          padding: isMobile ? "10px" : "20px",
          overflowY: 'auto',
          maxHeight: 'calc(85vh - 140px)' // adjust based on header and footer height
        }}>
          <Box
            position="relative"
            sx={{
              padding: isMobile ? "5px 5px" : "10px 10px",
              border: "1px solid #DDE3EA",
              borderRadius: "8px",
              backgroundColor: "#F5F7FA",
            }}
          >
            {/* <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography>Medication</Typography>
              <TextField
                placeholder="Add Medication"
                value={formValues.Medication}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    Medication: e.target.value,
                  });
                }}
                inputProps={{
                  autoComplete: "off",
                  style: {
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    border: "none", // ← Add this
                    outline: "none", // ← Add this too
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    color: "#000",
                    minHeight: "40px",
                    "& fieldset": { borderColor: "#fff" },
                    "&:hover fieldset": { borderColor: "#333" },
                    "&.Mui-focused fieldset": { borderColor: "#333" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>Dosage</Typography>
              <TextField
                placeholder="Add Dosage"
                value={formValues.Dosage}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    Dosage: e.target.value,
                  });
                }}
                inputProps={{
                  autoComplete: "off",
                  style: {
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    border: "none", // ← Add this
                    outline: "none", // ← Add this too
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    color: "#000",
                    minHeight: "40px",
                    "& fieldset": { borderColor: "#fff" },
                    "&:hover fieldset": { borderColor: "#333" },
                    "&.Mui-focused fieldset": { borderColor: "#333" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>Direstions for use</Typography>
              <TextField
                placeholder="Add direstions for use"
                value={formValues.Directions}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    Directions: e.target.value,
                  });
                }}
                inputProps={{
                  autoComplete: "off",
                  style: {
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    border: "none", // ← Add this
                    outline: "none", // ← Add this too
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    color: "#000",
                    minHeight: "40px",
                    "& fieldset": { borderColor: "#fff" },
                    "&:hover fieldset": { borderColor: "#333" },
                    "&.Mui-focused fieldset": { borderColor: "#333" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>Route</Typography>
              <TextField
                placeholder="Add Route"
                value={formValues.Route}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    Route: e.target.value,
                  });
                }}
                inputProps={{
                  autoComplete: "off",
                  style: {
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#000",
                    caretColor: "#000",
                    border: "none", // ← Add this
                    outline: "none", // ← Add this too
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    color: "#000",
                    minHeight: "40px",
                    "& fieldset": { borderColor: "#fff" },
                    "&:hover fieldset": { borderColor: "#333" },
                    "&.Mui-focused fieldset": { borderColor: "#333" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography>Need help with administration</Typography>
              <RadioGroup
                row
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="Yes"
                name="radio-buttons-group"
                value={formValues.NeedHelp}
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    NeedHelp: e.target.value,
                  });
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            <Grid item xs={6} sx={{ backgroundColor: "green" }}>
              <Button
                fullWidth
                sx={{
                  my: 2,
                  width: "120px",
                  bgcolor: "#7B7DE5",
                  color: "#fff",
                  textTransform: "none",
                  //   py: 1,
                  borderRadius: "10px",
                  "&:hover": { bgcolor: "#8E85F7" },
                }}
                onClick={handleAddMedicine}
              >
                Add
              </Button>
            </Grid>
          </Grid> */}

            <Grid container spacing={2} sx={{ marginTop: "5px" }}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography>Medication</Typography>
                <TextField
                  placeholder="Add Medication"
                  value={formValues.Medication}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      Medication: e.target.value,
                    });
                  }}
                  inputProps={{
                    maxLength: 30,
                    autoComplete: "off",
                  }}
                  sx={{
                    width: "100%",
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography>Dosage</Typography>
                <TextField
                  placeholder="Add Dosage"
                  value={formValues.Dosage}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      Dosage: e.target.value,
                    });
                  }}
                  inputProps={{
                    maxLength: 10,
                    autoComplete: "off"
                  }}
                  sx={{
                    width: "100%",
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography>Directions for use</Typography>
                <TextField
                  placeholder="Add directions for use"
                  value={formValues.Directions}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      Directions: e.target.value,
                    });
                  }}
                  inputProps={{
                    maxLength: 30,
                    autoComplete: "off"
                  }}
                  sx={{
                    width: "100%",
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography>Route</Typography>
                <TextField
                  placeholder="Add Route"
                  value={formValues.Route}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      Route: e.target.value,
                    });
                  }}
                  inputProps={{
                    maxLength: 13,
                    autoComplete: "off",
                    backgroundColor: "#FFFFFF",
                    borderColor: "#DDE3EA",
                  }}
                  sx={{
                    width: "100%",
                    borderColor: "#DDE3EA",
                    backgroundColor: "#FFFFFF",
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, backgroundColor: "#FFFFFF", padding: "10px", border: "1px solid #DDE3EA", borderRadius: "8px" }}>
                  <Typography sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReportProblemOutlinedIcon />
                    Does the patient require assistance taking this medication?
                  </Typography>
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={formValues.NeedHelp === "Yes"}
                        onChange={(e) => {
                          setFormValues({
                            ...formValues,
                            NeedHelp: e.target.checked ? "Yes" : "No",
                          });
                        }}
                      />
                    }
                    sx={{ mr: 0 }}
                  // label={formValues.NeedHelp === "Yes" ? "Yes" : "No"}
                  />
                </Box>

                {/* <RadioGroup
                  row
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="Yes"
                  name="radio-buttons-group"
                  value={formValues.NeedHelp}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      NeedHelp: e.target.value,
                    });
                  }}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup> */}


              </Grid>

            </Grid>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                startIcon={<ControlPointOutlinedIcon />}
                onClick={handleAddMedicine}
              >
                Add Medication
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{
            border: "1px solid #DDE3EA",
            borderRadius: "8px",
            mt: 2,
          }}>
            <Table aria-label="simple table" sx={{ fontSize: '12px' }}>
              <TableHead sx={{ backgroundColor: "#F5F7FA", }}>
                <TableRow>
                  <TableCell sx={style.tableHead}>Medication</TableCell>
                  <TableCell sx={style.tableHead}>Dosage</TableCell>
                  <TableCell sx={style.tableHead}>Direction for use</TableCell>
                  <TableCell sx={style.tableHead}>Route</TableCell>
                  <TableCell align="center" sx={style.tableHead}>
                    Needs Help
                    <br />
                    With Administration
                  </TableCell>
                  <TableCell sx={style.tableHead}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medications.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>{row.Medication}</TableCell>
                    <TableCell>{row.Dosage}</TableCell>
                    <TableCell>{row.Directions}</TableCell>
                    <TableCell>{row.Route}</TableCell>
                    <TableCell align="center">
                      {row.NeedHelp ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          const _medications = medications.filter(
                            (med, i) => i !== idx
                          );
                          setMedications(_medications);
                        }}
                      >
                        <DeleteOutlinedIcon sx={{color: "error.main"}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!medications.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No medications added
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </Box>


        {/* Save Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" backgroundColor={"#FCFCFC"} padding={"10px 15px"} borderTop="1px solid #D6D6D6">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
            {`${medications.length} medication(s) recorded`}
          </Typography>
          <div>
            <Button
              variant="outlined"
              sx={{
                color: "#7A7A7A",
                borderColor: "#D0D5DD",
                mr: 1,
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
            >
              {isLoading === true ? (
                <CircularProgress color="#fff" size={"24px"} />
              ) : (
                "Save Records"
              )}
            </Button>
          </div>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMedicationsPopup;
